import { useState, useEffect, useRef } from "react";
import { Nav, NavLink, NavItem, TabContent, TabPane } from "reactstrap";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import DataTable from "../../common/DataTable/DataTable";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../../features/auth/authSlice";
import {
  useGetIncidentDetailsQuery,
  useGetIncidentFormsQuery,
  useGetLinkedIncidentsQuery,
  useGetRelatedIncidentsQuery,
  useExportFormsMutation,
  useCompleteIncidentMutation,
  useUpdateIncidentFormMutation,
  useAddTaskToIncidentMutation,
  useDeleteAttachedDocumentMutation,
  useLinkDocumentToIncidentMutation,
  useInitiateClosureMutation,
  useCloseIncidentMutation,
  useDownloadAttachFileMutation,
} from "../../../features/Incidents/IncidentSlice";

import nextIcon from "../../../assets/images/icons/next.svg";
import backIcon from "../../../assets/images/icons/previous.svg";
import editIcon from "../../../assets/images/icons/edit.svg";
import closeIcon from "../../../assets/images/icons/close.svg";
import addIcon from "../../../assets/images/icons/addIcon.png";
import pdfIcon from "../../../assets/images/icons/pdfExportIcon.svg";
import deleteIcon from "../../../assets/images/icons/Delete.svg";
import downloadIcon from "../../../assets/images/icons/download.svg";
import readonlyIcon from "../../../assets/images/icons/readonly.svg";

import { format } from "date-fns";
import { nanoid } from "@reduxjs/toolkit";
import "./IncidentDetails.css";
import { Loader } from "../../common/Loader/Loader";
import DataForm from "../../common/DataForm/DataForm";
import { SurveyPDF } from "survey-pdf";
import { Model } from "survey-core";
import Timeline from "../../common/Timeline/Timeline";
import IncidentReportingModal from "../IncidentReportingModal/IncidentReportingModal";
import IncidentForm from "../IncidentForm/IncidentForm";
import toast, { Toaster } from "react-hot-toast";
import ConfirmationModal from "../../common/ConfirmationModal/ConfirmationModal";
import ClosureForm from "../ClosureForm/ClosureForm";
import { incidentStatus, taskStatus } from "../../../enums/statuses";
import { mergeObjectsFromArray } from "../../../utils/commonMethods";
import { taskTypes } from "../../../enums/taskTypes";
import AttachmentModal from "../../common/AttachmentModal/AttachmentModal";
import { userRoles } from "../../../enums/userRoles";
import AddTasksAndReviewsForm from "../AddTasksAndReviewsForm/AddTasksAndReviewsForm";

const IncidentDetails = () => {
  const { incidentId } = useParams();

  const closureFormIds = useRef();
  const setClosureFormIds = (newValue) => {
    closureFormIds.current = newValue;
  };

  const user = useSelector(selectCurrentUser);

  const {
    data: incidentDetails,
    isLoading,
    isSuccess,
    error: incidentDetailsError,
  } = useGetIncidentDetailsQuery(incidentId, { skip: !incidentId });

  const {
    data: incidentForms,
    isLoading: incidentFormsIsLoading,
    isFetching: incidentFormsIsFetching,
  } = useGetIncidentFormsQuery(incidentId, { skip: !incidentId });

  const { data: linkedIncidents, isLoading: linkedIncidentsIsLoading } =
    useGetLinkedIncidentsQuery(incidentId);

  const { data: relatedIncidents, isLoading: relatedIncidentsIsLoading } =
    useGetRelatedIncidentsQuery(incidentId);

  const [exportForms] = useExportFormsMutation();

  const [completeIncident, { isLoading: completeIncidentIsLoading }] =
    useCompleteIncidentMutation();

  const [closeIncident] = useCloseIncidentMutation();

  const [updateIncidentForm, { isLoading: updateIncidentFormIsLoading }] =
    useUpdateIncidentFormMutation();

  const [addTaskToIncident, { isLoading: addTaskToIncidentIsLoading }] =
    useAddTaskToIncidentMutation();

  const [triggerDownload, { isLoading: isDownloading }] =
    useDownloadAttachFileMutation();

  const [deleteAttachedDocument] = useDeleteAttachedDocumentMutation();

  const [linkDocumentToIncident] = useLinkDocumentToIncidentMutation();

  const [initiateClosure] = useInitiateClosureMutation();

  const [incidentDetailsData, setIncidentDetailsData] = useState(null);
  const [incidentFormsData, setIncidentFormsData] = useState([]);
  const [linkedIncidentsData, setLinkedIncidentsData] = useState([]);
  const [relatedIncidentsData, setRelatedIncidentsData] = useState([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [showTimeline, setShowtimeline] = useState(false);
  const [incidentModalIsOpen, setIncidentModalIsOpen] = useState(false);
  const [confirmModalIsOpen, setConfirmModalIsOpen] = useState(false);
  const [addTaskModalIsOpen, setAddTaskModalIsOpen] = useState(false);
  const [addAttachmentModalIsOpen, setAddAttachmentModalIsOpen] =
    useState(false);
  const [isCompleteIncident, setIsCompleteIncident] = useState(false);
  const [editable, setEditable] = useState(false);

  const [defaultValues, setDefaultValues] = useState(null);
  const [type, setType] = useState("full");

  const [columns, setColumns] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [attachments, setAttachments] = useState([]);

  const { taskTab } = useParams();
  const [activeTab, setActiveTab] = useState(1);
  const navigate = useNavigate();
  const [taskType, setTaskType] = useState(taskTypes.NORMAL);

  const [closureInitiated, setClosureInitiated] = useState(false);

  const isReadOnly = !user?.roles?.some((role) =>
    [userRoles.ADMIN, userRoles.ENHANCED_MANAGER, userRoles.MANAGER].includes(
      role
    )
  );

  useEffect(() => {
    if (taskTab) {
      setActiveTab(taskTab ? Number.parseInt(taskTab) : 97);
    }
  }, [taskTab, setActiveTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  useEffect(() => {
    if (incidentDetails) {
      const data = incidentDetails.ids.map((id) => {
        return incidentDetails.entities[id];
      });
      setIncidentDetailsData(...data);
      setColumns(generateColumns(data[0].tasks));
      const tasks = data[0].tasks.filter(
        (t) => t?.taskType?.id === taskTypes.NORMAL
      );
      const reviews = data[0].tasks.filter(
        (t) => t?.taskType?.id === taskTypes.REVIEW
      );
      setTasks(tasks);
      setReviews(reviews);
      setAttachments(
        data[0]?.linkDocuments?.map((docs) => ({
          ...docs?.attachment,
          isTaskAttachment: docs?.isTaskAttachment,
          taskId: docs?.incidentTask?.id,
        }))
      );
    }

    if (incidentDetailsError) {
      toast.error(
        `${
          incidentDetailsError?.data?.Message ||
          incidentDetailsError?.data?.title ||
          "Something unexpected happened while fetching incident"
        }`
      );
      if (incidentDetailsError?.status === 403) {
        navigate("/incidents");
      }
    }
  }, [incidentDetails, incidentDetailsError, navigate]);

  useEffect(() => {
    if (incidentForms) {
      const data = incidentForms.ids.map((id) => {
        return incidentForms.entities[id];
      });
      const formData = mergeObjectsFromArray(data.map((d) => d.data));

      setDefaultValues({
        ...formData,
      });
      setIncidentFormsData(data);
    }
  }, [incidentForms]);

  useEffect(() => {
    if (linkedIncidents) {
      const data = linkedIncidents.ids.map((id) => {
        return linkedIncidents.entities[id];
      });
      setLinkedIncidentsData(data);
    }
  }, [linkedIncidents]);

  useEffect(() => {
    if (relatedIncidents) {
      const data = relatedIncidents.ids.map((id) => {
        return relatedIncidents.entities[id];
      });
      setRelatedIncidentsData(data);
    }
  }, [relatedIncidents]);

  const toggleModal = () => {
    setModalIsOpen(!modalIsOpen);
  };

  const toggleAddTaskModal = () => {
    setAddTaskModalIsOpen((prev) => !prev);
  };

  const toggleAddAttachmentModal = () => {
    setAddAttachmentModalIsOpen((prev) => !prev);
  };

  const goBack = () => {
    setShowtimeline(false);
    setActiveTab(1);
  };

  const toggleConfirmModal = () => {
    setConfirmModalIsOpen((prev) => !prev);
  };

  const goToTimeline = () => {
    handleTabChange(98);
    setShowtimeline(true);
  };

  const handleModalClose = () => {
    setIsCompleteIncident(false);
    setIncidentModalIsOpen((prev) => !prev);
  };

  const statusId = incidentDetailsData?.status?.id;
  const statusClassName =
    statusId === incidentStatus.OPEN
      ? "ongoing"
      : statusId === incidentStatus.CLOSED
      ? "closed"
      : statusId === incidentStatus.CLOSURE_INITIATED
      ? "incomplete"
      : statusId === incidentStatus.CLOSURE_COMPLETED
      ? "complete"
      : "";

  const generateColumns = (data) => {
    if (data.length <= 0) {
      return;
    }
    const desiredOrder = [
      "id",
      "name",
      "dueDate",
      "status",
      "createdOn",
      "assignedTo",
    ];

    const columns = desiredOrder
      .map((field) => {
        if (field === "progress") {
          return null;
        }

        let columnConfig = {
          key: nanoid(),
          field,
          headerText: field
            .replace(/([A-Z])/g, " $1")
            .trim()
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
          alignment: "left",
          template: null,
          width: "180px",
        };

        if (field === "id") {
          columnConfig.headerText = "ID";
          columnConfig.width = "100px";
          columnConfig.template = (props) => {
            return <p className="m-0">{props[field]}</p>;
          };
        }

        if (field === "name") {
          columnConfig.headerText = `Name`;
          columnConfig.template = (props) => {
            return <p className="m-0 fw-bold">{props[field]}</p>;
          };
          columnConfig.width = "500px";
        }

        if (field === "status") {
          columnConfig.template = (props) => {
            let style;
            switch (props.status?.id) {
              case taskStatus.COMPLETED:
                style = "status-pill complete";
                break;
              case taskStatus.INPROGRESS:
                style = "status-pill ongoing";
                break;

              default:
                style = "";
            }
            return <span className={style}>{props[field]?.description}</span>;
          };
        }

        if (field === "createdOn") {
          columnConfig.template = (props) => {
            return <span>{format(props[field], "dd/MM/yyyy")}</span>;
          };
        }
        if (field === "assignedTo") {
          columnConfig.template = (props) => {
            return (
              <div className="d-flex gap-1 flex-wrap">
                {props.assignedTo.map((assignee) => (
                  <span
                    key={assignee.id}
                    className="me-1 bg-success text-white p-1 rounded"
                  >
                    {assignee.name}
                  </span>
                ))}
              </div>
            );
          };
          columnConfig.width = "260px";
        }
        if (field === "dueDate") {
          columnConfig.headerText = "Due On";
          columnConfig.template = (props) => {
            return <span>{format(props[field], "dd/MM/yyyy")}</span>;
          };
        }

        return columnConfig;
      })
      .filter(Boolean);

    return columns;
  };

  function createSurveyPdfModel(response) {
    const surveyModel = new Model(response.structure);

    let pdfWidth =
      !!surveyModel && surveyModel.pdfWidth ? surveyModel.pdfWidth : 210;
    let pdfHeight =
      !!surveyModel && surveyModel.pdfHeight ? surveyModel.pdfHeight : 297;
    let options = {
      fontSize: 14,
      margins: {
        left: 10,
        right: 10,
        top: 10,
        bot: 10,
      },

      format: [pdfWidth, pdfHeight],
      readonlyRenderAs: "text",
    };
    const surveyPDF = new SurveyPDF(response.structure, options);
    if (surveyModel) {
      surveyPDF.data = response.data;
      surveyPDF.mode = "display";
    }

    return surveyPDF;
  }
  function saveSurveyToPdf(filename, surveyModel) {
    createSurveyPdfModel(surveyModel).save(filename);
  }

  const generatePDF = async () => {
    try {
      const responsePromise = exportForms({ incidentId, type }).unwrap();

      toast.promise(responsePromise, {
        loading: "Exporting...",
        success: "Downloading Successfull",
        error: "Error when exporting",
      });

      const response = await responsePromise;
      const formatedResponse = {
        ...response,
        data: { IncidentId: incidentDetailsData?.id, ...response.data },
        structure: {
          ...response.structure,
          pages: [
            {
              ...response.structure.pages[0],
              elements: [
                {
                  type: "text",
                  name: "IncidentId",
                  title: "Incident ID",
                  isRequired: true,
                  readOnly: true,
                },
                ...response.structure.pages[0].elements,
              ],
            },
            ...response.structure.pages.slice(1),
          ],
        },
      };

      saveSurveyToPdf(
        `${incidentDetailsData?.name ?? "Incident"}.pdf`,
        formatedResponse
      );
    } catch (error) {
      toast.error(error?.data?.Message || "An unexpected error occurred");
    }
  };

  const handleEditForms = () => {
    setEditable((prev) => !prev);
  };
  const handleUpdateForm = async (formData) => {
    try {
      const data = {
        incidentId,
        formVersionId: activeTab,
        formData,
      };

      await updateIncidentForm(data).unwrap();

      toast.success("Form Data Updated Successfully");
    } catch (error) {
      toast.error(
        error?.data?.Message || "An error occurred while updating the form data"
      );
    }
  };

  const submitClosureForm = async (formData) => {
    const data = {
      incidentId,
      formData,
    };

    try {
      await completeIncident(data).unwrap();
      toast.success("Incident completed Successfully");
      navigate("/incidents");
    } catch (error) {
      handleModalClose();
      toast.error(error?.data?.Message);
    }
  };

  const handleAddTaskToIncident = () => {
    toggleAddTaskModal();
  };

  const handleAddTask = async (sender) => {
    const formData = sender.data;
    try {
      await addTaskToIncident({
        id: incidentId,
        formData: {
          ...formData,
          taskTypeId: taskType,
          assignedTo: formData?.assignedTo?.map((assignee) => ({
            userId: assignee,
          })),
        },
      }).unwrap();
      toast.success(
        `${
          taskType === taskTypes.NORMAL ? "Task" : "Review"
        } added successfully`
      );
      toggleAddTaskModal();
    } catch (error) {
      toast.error(error?.data?.Message);
    }
  };

  const goToViewTaskPage = (args, tab) => {
    navigate(`/my-tasks/view/${args?.data?.guid}`, {
      state: { from: incidentId, tab },
    });
  };

  const handleOptionChange = (event) => {
    setType(event.target.value);
  };

  const linkDocument = async (id) => {
    try {
      if (id) {
        await linkDocumentToIncident({
          attachmentDocumentId: id,
          id: incidentId,
        }).unwrap();
      }
      toast.success("Attachment added Successfully...!", { duration: 5000 });
    } catch (error) {
      toast.error(error?.data?.Message || error?.data?.title);
    }
  };

  const handleDeleteAttachedDocument = async (id) => {
    try {
      const requestPromise = deleteAttachedDocument(id).unwrap();
      toast.promise(requestPromise, {
        success: "Attachment Deleted Successfully...!",
        loading: "Deleting...",
        error: "Something Went Wrong...!",
      });
      await requestPromise;
    } catch (error) {
      toast.error(error?.data?.Message);
    }
  };

  const handleAttachmentDownload = async (attachmentId, attachmentName) => {
    try {
      const responsePromise = triggerDownload({
        id: attachmentId,
        filename: attachmentName,
      }).unwrap();

      toast.promise(responsePromise, {
        loading: "Downloading...",
        success: "Downloaded Successfully...!",
        error: (err) =>
          `${
            err?.data?.Message ||
            err?.data?.title ||
            "Something went wrong while downloading attachment...!"
          }`,
      });
      await responsePromise;
    } catch (error) {
      console.error(error);
    }
  };

  const attachmentColoumns = [
    {
      key: nanoid(),
      field: "id",
      headerText: "ID",
      alignment: "left",
      template: null,
      width: "80px",
    },
    {
      key: nanoid(),
      field: "name",
      headerText: "File Name",
      alignment: "left",
      template: null,
      width: "250px",
    },
    {
      key: nanoid(),
      field: "description",
      headerText: "File Description",
      alignment: "left",
      template: null,
      width: "250px",
    },
    {
      key: nanoid(),
      field: "createdOn",
      headerText: "Created On",
      alignment: "left",
      template: (props) => (
        <span>
          {props?.createdOn
            ? format(new Date(props?.createdOn), "dd/MM/yyyy")
            : "-"}
        </span>
      ),
      width: "150px",
    },
    {
      key: nanoid(),
      field: "createdBy",
      headerText: "Created By",
      alignment: "left",
      template: (props) => <span>{props.createdBy?.name}</span>,
      width: "200px",
    },
    {
      key: nanoid(),
      field: "relatedTask",
      alignment: "center",
      headerText: "Related Task",
      template: (props) => (
        <div>
          {props.isTaskAttachment && (
            <button
              className="btn btn-sm p-0 link-btn"
              title="Go to Task"
              onClick={() => navigate(`/my-tasks/view/${props?.taskId}`)}
            >
              {`#${props?.taskId}`}
            </button>
          )}
        </div>
      ),
      width: "100px",
    },
    {
      key: nanoid(),
      field: "options",
      headerText: "Options",
      template: (props) => (
        <div>
          <button
            className="e-btn e-flat option-btn me-1"
            title="Download File"
            disabled={isDownloading}
            onClick={() => handleAttachmentDownload(props?.id, props?.name)}
          >
            <img src={downloadIcon} alt="download" width={25} />
          </button>

          {!props.isTaskAttachment && (
            <button
              className="e-btn e-flat option-btn"
              title="Delete File"
              onClick={() => handleDeleteAttachedDocument(props?.id)}
            >
              <img src={deleteIcon} alt="delete" width={25} />
            </button>
          )}
        </div>
      ),
      width: "150px",
    },
  ];

  const handleClosureConfirmationModal = () => {
    if (incidentDetailsData?.status?.id === incidentStatus.OPEN) {
      setConfirmModalIsOpen(true);
    } else return;
  };

  const handleInitiateClosure = async () => {
    try {
      const requestPromise = initiateClosure({
        id: incidentId,
        formsData: closureFormIds.current,
      }).unwrap();

      toast.promise(requestPromise, {
        success: "Closure Initiated",
        loading: "Initiating...",
        error: (err) =>
          `${
            err?.data?.Message ||
            err?.data?.title ||
            "Unexpected error occured while initiating closure...!"
          }`,
      });

      await requestPromise;
      setClosureInitiated(false);
      toggleConfirmModal();
    } catch (error) {
      console.error(error?.data?.Message || error?.data?.title);
    }
  };

  const handleCloseIncident = async () => {
    try {
      const requestPromise = closeIncident(incidentId).unwrap();
      toast.promise(requestPromise, {
        loading: "Closing...",
        success: "Incident Closed",
        error: (err) =>
          `${
            err?.data?.Message ||
            err?.data?.title ||
            "Unexpected error occured while closing the incident...!"
          }`,
      });
      await requestPromise;
      toggleConfirmModal();
    } catch (error) {
      toast.error(error?.data?.Message || error?.data?.title);
    }
  };

  return (
    <div className="incident-details">
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#0b463d",
            color: "#ffffff",
          },
          success: {
            iconTheme: {
              secondary: "#ffffff",
            },
          },
        }}
      />
      {(isLoading ||
        incidentFormsIsLoading ||
        incidentFormsIsFetching ||
        relatedIncidentsIsLoading ||
        updateIncidentFormIsLoading ||
        completeIncidentIsLoading) && (
        <div className="loader-container">
          <Loader />
        </div>
      )}
      <div className="row mb-1 justify-content-between align-items-center">
        <div className="col-auto">
          <h4 className="p-2 m-0">
            Incident Review - Incident {incidentDetailsData?.id}
          </h4>
        </div>

        <div className="col-auto">
          <div className="dropdown" style={{ display: "inline-block" }}>
            <button
              className="btn btn-green px-3 me-2 mb-1"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <span className="me-2">Export to PDF</span>
              <img src={pdfIcon} width={22} alt="pdf" />
            </button>
            <ul className="dropdown-menu px-4">
              <div className="form-check">
                <input
                  className="form-check-input custom-radio-button"
                  type="radio"
                  name="type"
                  id="typeFull"
                  checked={type === "full"}
                  value="full"
                  onChange={handleOptionChange}
                />
                <label className="form-check-label" htmlFor="typeFull">
                  Full
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input custom-radio-button"
                  type="radio"
                  name="type"
                  id="typePartial"
                  value="partial"
                  checked={type === "partial"}
                  onChange={handleOptionChange}
                />
                <label className="form-check-label" htmlFor="typePartial">
                  Partial
                </label>
              </div>
              <li className="text-center">
                <button
                  className="btn btn-green btn-sm px-3 mx-auto"
                  onClick={generatePDF}
                >
                  export
                </button>
              </li>
            </ul>
          </div>

          {incidentDetailsData?.status?.id ===
            incidentStatus.CLOSURE_COMPLETED &&
            incidentDetailsData?.canComplete &&
            !incidentDetailsData?.masterIncident && (
              <button
                className="btn btn-green px-3 me-2 mb-1"
                onClick={() => {
                  setClosureInitiated(false);
                  toggleConfirmModal();
                }}
              >
                <span className="me-2">Close Incident</span>
              </button>
            )}

          {incidentDetailsData?.status?.id === incidentStatus.OPEN &&
            incidentDetailsData?.canComplete &&
            !incidentDetailsData?.masterIncident && (
              <button
                className="btn btn-green px-3 me-2 mb-1"
                onClick={() => {
                  setClosureInitiated(true);
                  handleClosureConfirmationModal();
                }}
                disabled={isReadOnly}
              >
                <span className="me-2">Initiate Closure</span>
              </button>
            )}
          {!incidentDetailsData?.isSensitive &&
            incidentDetailsData?.status?.id !== incidentStatus.CLOSED && (
              <>
                <button
                  className="btn btn-green px-3 me-2 mb-1"
                  onClick={() => {
                    navigate(
                      `/incidents/new?incidentRelation=related&relatedIncidentId=${
                        incidentDetailsData?.masterIncident?.guid || incidentId
                      }`
                    );
                  }}
                >
                  <span className="me-2">Create Related Incident</span>
                  <img src={addIcon} width={22} alt="add" />
                </button>
              </>
            )}
        </div>
      </div>

      <div className="card custom-card incident-details-card">
        <div className="card-body">
          <div className="row mb-5">
            <div className="col">
              <div className="card custom-card img-card banner">
                <div className="">
                  <div className="card-body custom-card-body">
                    <span
                      className={`status-pill custom-pill mb-3 ${statusClassName}  `}
                    >
                      {incidentDetailsData?.status?.description}
                    </span>
                    <p className="fs-4 mb-3">
                      <strong>Incident: </strong> {incidentDetailsData?.name}
                    </p>
                    <p className="fs-6 mb-3">
                      <strong>Submitted By: </strong>
                      {incidentDetailsData?.createdBy?.name ?? "-"}
                      {` (${incidentDetailsData?.createdBy?.email ?? "-"})`}
                    </p>
                    {incidentDetailsData?.masterIncident && (
                      <div>
                        <h6 className="d-inline-block me-2">
                          Master Incident :
                        </h6>
                        <Link
                          className="btn-master-incident px-3 py-2 rounded shadow"
                          to={`/incidents/view/${incidentDetailsData?.masterIncident?.guid}`}
                        >{`#${incidentDetailsData?.masterIncident?.id} - ${incidentDetailsData?.masterIncident?.name}`}</Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row ">
            <div className="col">
              <div>
                <Nav pills className="fixed-tabs-nav">
                  {!showTimeline ? (
                    <>
                      {incidentFormsData.map((form) => (
                        <NavItem key={form.id}>
                          <NavLink
                            className={`custom-tab-link ${
                              activeTab === form.id ? "active" : ""
                            }`}
                            onClick={() => handleTabChange(form.id)}
                          >
                            {form.description}
                            {!form?.canEdit && (
                              <img
                                src={readonlyIcon}
                                className="ms-2"
                                width={14}
                                height={14}
                                alt="readonly"
                                title="Read Only"
                              />
                            )}
                          </NavLink>
                        </NavItem>
                      ))}

                      {incidentDetailsData?.canEditTasks && (
                        <NavItem>
                          <NavLink
                            className={`custom-tab-link ${
                              activeTab === 95 ? "active" : ""
                            }`}
                            onClick={() => handleTabChange(95)}
                          >
                            Reviews
                          </NavLink>
                        </NavItem>
                      )}
                      {incidentDetailsData?.canEditTasks && (
                        <NavItem>
                          <NavLink
                            className={`custom-tab-link ${
                              activeTab === 97 ? "active" : ""
                            }`}
                            onClick={() => handleTabChange(97)}
                          >
                            Tasks
                          </NavLink>
                        </NavItem>
                      )}
                      <NavItem>
                        <NavLink
                          className={`custom-tab-link ${
                            activeTab === 96 ? "active" : ""
                          }`}
                          onClick={() => handleTabChange(96)}
                        >
                          Attachments
                        </NavLink>
                      </NavItem>

                      {incidentDetailsData?.canEdit &&
                        activeTab !== 97 &&
                        activeTab !== 96 &&
                        activeTab !== 95 && (
                          // activeTab !== 93 &&
                          <NavItem>
                            <button
                              className="option-btn m-1 border-0 "
                              onClick={handleEditForms}
                            >
                              {!editable ? (
                                <img
                                  src={editIcon}
                                  alt="edit"
                                  width={26}
                                  className="p-1"
                                />
                              ) : (
                                <img
                                  src={closeIcon}
                                  alt="close"
                                  width={26}
                                  className="p-1"
                                />
                              )}
                            </button>
                          </NavItem>
                        )}

                      <button
                        className="btn btn-green-outline px-3 ms-auto"
                        onClick={goToTimeline}
                      >
                        <span className="me-2">Timeline View</span>{" "}
                        <img src={nextIcon} width={22} alt="" />
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-green-outline px-3 me-auto"
                      onClick={goBack}
                    >
                      <img src={backIcon} width={22} alt="" />
                      <span className="ms-2">Back</span>{" "}
                    </button>
                  )}
                </Nav>
                <TabContent activeTab={activeTab} className="tab-content">
                  {incidentFormsData.map((form) => (
                    <TabPane key={form.id} tabId={form.id}>
                      <div className="row">
                        <div className="col">
                          {form?.description === "Closure" &&
                            relatedIncidentsData?.length > 0 && (
                              <div className="container text-center text-danger mb-1">
                                <h6>RELATED INCIDENT CLOSURE</h6>
                                <p>
                                  Please note that any comments entered in this
                                  tab will be shared across all related
                                  incidents within this series. There may be
                                  multiple contributors within the closure
                                  process. Please do not remove or edit the text
                                  whilst the sections are being drafted. The
                                  Senior Incident Manager will confirm the final
                                  text before the incident is closed.
                                </p>
                              </div>
                            )}
                          <DataForm
                            page="incidentDetails"
                            data={form.structure}
                            formData={{
                              currentFormData: form.data,
                              defaultValues,
                              formId: form.id,
                            }}
                            readOnly={!form?.canEdit || !editable}
                            handleSave={handleUpdateForm}
                          />
                        </div>
                      </div>
                    </TabPane>
                  ))}
                  <TabPane tabId={97}>
                    <button
                      className="btn btn-green px-3 me-2"
                      onClick={() => {
                        setTaskType(taskTypes.NORMAL);
                        handleAddTaskToIncident();
                      }}
                    >
                      <span className="me-2">Add Task</span>
                      <img src={addIcon} width={22} alt="" />
                    </button>
                    <div className="row mt-3">
                      <div className="col">
                        {isSuccess ? (
                          tasks?.length > 0 ? (
                            <DataTable
                              data={tasks}
                              columns={columns}
                              height={440}
                              rowSelected={(args) => goToViewTaskPage(args, 97)}
                            />
                          ) : (
                            "No tasks available..."
                          )
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  </TabPane>
                  <TabPane tabId={98}>
                    <div className="row mt-3">
                      <div className="col">
                        <Timeline
                          backToDetailsPage={goBack}
                          linkedIncidents={linkedIncidentsData}
                          currentIncident={incidentDetailsData}
                          data={relatedIncidentsData}
                        />
                      </div>
                    </div>
                  </TabPane>
                  <TabPane tabId={96}>
                    <button
                      className="btn btn-green px-3 me-2"
                      onClick={toggleAddAttachmentModal}
                    >
                      <span className="me-2">Add Attachment</span>
                      <img src={addIcon} width={22} alt="" />
                    </button>
                    <div className="row mt-3">
                      <div className="col">
                        <DataTable
                          data={attachments}
                          columns={attachmentColoumns}
                          height={440}
                          rowSelected={() => {}}
                        />
                      </div>
                    </div>
                  </TabPane>
                  <TabPane tabId={95}>
                    <button
                      className="btn btn-green px-3 me-2"
                      onClick={() => {
                        setTaskType(taskTypes.REVIEW);
                        handleAddTaskToIncident();
                      }}
                    >
                      <span className="me-2">Add Review</span>
                      <img src={addIcon} width={22} alt="" />
                    </button>
                    <div className="row mt-3">
                      <div className="col">
                        <DataTable
                          data={reviews}
                          columns={columns}
                          height={440}
                          rowSelected={(args) => goToViewTaskPage(args, 95)}
                        />
                      </div>
                    </div>
                  </TabPane>
                  <TabPane tabId={94}>
                    <div className="row mt-3">
                      <div className="col">
                        <ClosureForm
                          incidentId={incidentId}
                          handleSave={submitClosureForm}
                          isLoading={completeIncidentIsLoading}
                          setClosureFormIds={setClosureFormIds}
                          defaultValues={{
                            IncidentGroup: incidentDetailsData?.group?.id,
                            IncidentType: incidentDetailsData?.type?.id,
                            IncidentSubType: incidentDetailsData?.subType?.id,
                          }}
                        />
                      </div>
                    </div>
                  </TabPane>
                  {/* <TabPane tabId={93}>
                    <div className="row mt-3">
                      <div className="col">
                        <div className="task-description">
                          <form
                            id="incidentOutcomeForm"
                            onSubmit={(e) => handleUpdateIncident(e)}
                          >
                            <div className="mb-3">
                              <textarea
                                ref={outcome}
                                className="form-control border-0"
                                rows="12"
                                disabled={
                                  incidentDetailsData?.masterIncident ||
                                  updateIncidentIsLoading
                                }
                                defaultValue={
                                  incidentDetailsData?.outcome ?? ""
                                }
                              ></textarea>
                            </div>
                            {!incidentDetailsData?.masterIncident && (
                              <div>
                                <button
                                  className="btn btn-green me-3"
                                  style={{ minWidth: "180px" }}
                                  type="submit"
                                  disabled={updateIncidentIsLoading}
                                >
                                  Save
                                </button>

                                <button
                                  className="btn btn-secondary"
                                  style={{ minWidth: "180px" }}
                                  type="button"
                                  onClick={() =>
                                    (outcome.current.value =
                                      incidentDetailsData?.outcome)
                                  }
                                >
                                  Reset
                                </button>
                              </div>
                            )}
                          </form>
                        </div>
                      </div>
                    </div>
                  </TabPane> */}
                </TabContent>
              </div>
            </div>
          </div>
          <Modal
            isOpen={modalIsOpen}
            toggle={toggleModal}
            fade={false}
            backdrop={"static"}
            className=""
            centered
            size="xl"
            scrollable={true}
          >
            <ModalHeader className="border-0" toggle={toggleModal}>
              Linked Incidents
            </ModalHeader>
            <ModalBody>
              {linkedIncidentsIsLoading && <Loader />}
              {linkedIncidentsData.length > 0
                ? linkedIncidentsData.map((incident, index) => (
                    <div className="linked-incident" key={incident.id}>
                      <span>
                        {++index}. {incident.name}
                      </span>
                      <button
                        className="btn"
                        onClick={() => {
                          toggleModal();
                          navigate(`/incidents/view/${incident.guid}`);
                        }}
                      >
                        <img src={nextIcon} alt="next" />
                      </button>
                    </div>
                  ))
                : "No Linked incidents available"}
            </ModalBody>
          </Modal>
          <IncidentReportingModal
            isOpen={incidentModalIsOpen}
            toggle={handleModalClose}
          >
            {!isCompleteIncident && (
              <IncidentForm
                readOnly={editable}
                defaultValues={{
                  IncidentGroup: incidentDetailsData?.group?.id,
                  IncidentType: incidentDetailsData?.type?.id,
                  IncidentSubType: incidentDetailsData?.subType?.id,
                }}
                selectedTypes={{
                  typeId: incidentDetailsData?.type?.id,
                  subTypeId: incidentDetailsData?.subType?.id,
                }}
                fromIncidentId={incidentDetailsData?.id}
                isLinkedIncident={false}
                toggleModal={handleModalClose}
              />
            )}
          </IncidentReportingModal>
          <Modal
            isOpen={addTaskModalIsOpen}
            toggle={toggleAddTaskModal}
            fade={false}
            backdrop={"static"}
            className=""
            centered
            size="xl"
            scrollable={true}
          >
            <ModalHeader className="border-0" toggle={toggleAddTaskModal}>
              Add {taskType === taskTypes.NORMAL ? "Task" : "Review"} to
              Incident
            </ModalHeader>
            <ModalBody>
              {addTaskToIncidentIsLoading ? (
                <Loader />
              ) : (
                <AddTasksAndReviewsForm
                  handleSave={handleAddTask}
                  taskType={taskType}
                  isReadOnly={isReadOnly}
                />
              )}
            </ModalBody>
          </Modal>

          <AttachmentModal
            isOpen={addAttachmentModalIsOpen}
            toggle={toggleAddAttachmentModal}
            fn={linkDocument}
          />
          <ConfirmationModal
            title="incident"
            action={
              closureInitiated ? handleInitiateClosure : handleCloseIncident
            }
            toggle={toggleConfirmModal}
            isOpen={confirmModalIsOpen}
            actionType={"close"}
            isClosure={closureInitiated}
          />
        </div>
      </div>
    </div>
  );
};

export default IncidentDetails;
